import { useCallback, useEffect, useRef, useState } from "react";
import { mappingApi } from "@services/mappingApi";
import { ApiError } from "@utils/httpClient";
import { saveErrorMessage, withSaveRetry } from "@utils/saveRetry";
import {
  ReviewStatus,
  type DisplayConfig,
  type RowEdit,
  type RowEditInput,
  type SearchedMatch,
} from "@models/job";
import type { MappingResultRow, TmVariant } from "@models/mappingResult";
import { clearLegacyEditsStorage } from "./useJobResults.storage";
import {
  toPatchPayload,
  type BulkEditEntry,
  type BulkEditResult,
  type RowSaveStatus,
  type UseJobResultsState,
} from "./useJobResults.types";

export type {
  RowSaveStatus,
  UseJobResultsState,
} from "./useJobResults.types";

// The API caps `limit` at 500. Asking for the cap trims round trips on a big
// job, but it does NOT decide how many rows come back: a DynamoDB Query stops
// at 1 MB of data read BEFORE `Limit` applies, and a result row is heavy on the
// read side (the projection has to include `alternatives` so the API can
// resolve a corrected row's chosen match). Measured on develop, that ceiling
// lands around 110-130 rows per request whatever we ask for — so this is a
// modest saving, not a way to fetch a whole job in one call. The cursor walk
// below is what actually reads everything, and it stays required.
const PAGE_SIZE = 500;
const NOT_READY_RETRY_MS = 4_000;
const BULK_CONCURRENCY = 5;

/** How one row save ended. `superseded`: a newer edit of the same row was
 *  queued behind it, so its result no longer decides anything. */
type SaveOutcome = "saved" | "failed" | "superseded";

export const useJobResults = (
  jobId: string | null,
  statusVersion: number,
): UseJobResultsState => {
  const [rows, setRows] = useState<MappingResultRow[]>([]);
  // Optimistic edits not yet confirmed by the server: in flight, or failed and
  // waiting for Retry/Discard. In memory only. They used to be mirrored to
  // localStorage, which turned a failed save into an invisible edit that came
  // back on every reload and blocked the Excel export with no way to see or
  // retry it (AB#840758). The PATCH sends the absolute verdict, so an edit
  // lost to a closed tab is just a click to redo.
  const [edits, setEdits] = useState<Map<number, RowEdit>>(new Map());
  // The same map, readable after an `await` (state in a closure is stale by
  // then). Every write goes through `updateEdits`, which keeps both in step.
  const editsRef = useRef<Map<number, RowEdit>>(new Map());
  /** The tail of each row's save queue; saves of one row never overlap. */
  const rowQueueRef = useRef<Map<number, Promise<SaveOutcome>>>(new Map());
  /** Sequence number of each row's newest edit (last write wins). */
  const latestSeqRef = useRef<Map<number, number>>(new Map());
  const seqRef = useRef(0);
  /** Bumped on job change: saves from the previous job must not touch state. */
  const jobGenRef = useRef(0);
  const [saveStates, setSaveStates] = useState<Map<number, RowSaveStatus>>(
    new Map(),
  );
  const [cursor, setCursor] = useState<string | null>(null);
  const [display, setDisplay] = useState<DisplayConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notReady, setNotReady] = useState(false);
  const retryTimerRef = useRef<number | null>(null);
  const savedFadeTimersRef = useRef<Map<number, number>>(new Map());
  /** Generation counter for pagination chains. Each first-page fetch starts a
   * new generation; a chain whose generation is stale (a refresh, a SignalR
   * status bump, or an unmount started a newer one) drops its response
   * instead of applying it. Without this, two chains interleave and the same
   * row lands in `rows` twice — duplicate React keys corrupt the table. */
  const fetchGenRef = useRef(0);

  const updateEdits = useCallback(
    (update: (prev: Map<number, RowEdit>) => Map<number, RowEdit>): void => {
      const next = update(editsRef.current);
      if (next === editsRef.current) return;
      editsRef.current = next;
      setEdits(next);
    },
    [],
  );

  const setSaveState = useCallback(
    (rowIndex: number, status: RowSaveStatus | null): void => {
      setSaveStates((prev) => {
        const next = new Map(prev);
        if (status === null) next.delete(rowIndex);
        else next.set(rowIndex, status);
        return next;
      });
    },
    [],
  );

  const scheduleSavedFade = useCallback(
    (rowIndex: number): void => {
      const existing = savedFadeTimersRef.current.get(rowIndex);
      if (existing !== undefined) window.clearTimeout(existing);
      const handle = window.setTimeout(() => {
        savedFadeTimersRef.current.delete(rowIndex);
        setSaveState(rowIndex, null);
      }, 2_000);
      savedFadeTimersRef.current.set(rowIndex, handle);
    },
    [setSaveState],
  );

  const cancelRetry = useCallback((): void => {
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const fetchPage = useCallback(
    async (
      targetJobId: string,
      nextCursor: string | null,
      generation?: number,
    ): Promise<void> => {
      const isFirstPage = nextCursor === null;
      // A call without a generation starts a NEW chain and invalidates every
      // older one (their in-flight responses are dropped below).
      const gen = generation ?? ++fetchGenRef.current;
      if (isFirstPage) {
        setLoading(true);
        setError(null);
        setNotReady(false);
      } else {
        setLoadingMore(true);
      }
      try {
        const response = await mappingApi.getJobResult(targetJobId, {
          cursor: nextCursor ?? undefined,
          limit: PAGE_SIZE,
        });
        if (gen !== fetchGenRef.current) return; // stale chain — drop it
        setRows((prev) => {
          if (isFirstPage) return response.rows;
          // Belt-and-braces: never let the same row_index in twice even if a
          // page boundary shifts under us mid-walk (rows added/edited while
          // paging) — duplicate keys corrupt the table render.
          const seen = new Set(prev.map((r) => r.rowIndex));
          return [...prev, ...response.rows.filter((r) => !seen.has(r.rowIndex))];
        });
        setCursor(response.cursor);
        if (response.display !== undefined) setDisplay(response.display);
        // Show everything: keep pulling pages until the cursor is exhausted
        // so the TM review table renders the whole result set (no manual
        // "load more"). Each follow-up page accumulates onto `rows`.
        if (response.cursor) {
          void fetchPage(targetJobId, response.cursor, gen);
        }
      } catch (err) {
        if (gen !== fetchGenRef.current) return; // stale chain — stay quiet
        if (err instanceof ApiError && err.status === 409) {
          setNotReady(true);
          cancelRetry();
          retryTimerRef.current = window.setTimeout(() => {
            void fetchPage(targetJobId, null);
          }, NOT_READY_RETRY_MS);
          return;
        }
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Failed to load results";
        setError(message);
      } finally {
        if (isFirstPage) setLoading(false);
        else setLoadingMore(false);
      }
    },
    [cancelRetry],
  );

  // Edits and save states belong to the JOB, not to one fetch of it: reset them
  // only when the job changes. Resetting them on every status bump (SignalR
  // bumps `statusVersion` while a job is being reviewed) wiped the "Save
  // failed / Retry" marker and left an unsaved edit nobody could see.
  useEffect(() => {
    jobGenRef.current += 1;
    rowQueueRef.current = new Map();
    latestSeqRef.current = new Map();
    editsRef.current = new Map();
    setEdits(new Map());
    setSaveStates(new Map());
    for (const handle of savedFadeTimersRef.current.values()) {
      window.clearTimeout(handle);
    }
    savedFadeTimersRef.current.clear();
    if (jobId) clearLegacyEditsStorage(jobId);
  }, [jobId]);

  useEffect(() => {
    cancelRetry();
    fetchGenRef.current += 1; // invalidate any chain from the previous job/version
    setRows([]);
    setCursor(null);
    setDisplay(null);
    setError(null);
    setNotReady(false);
    if (!jobId) return;
    void fetchPage(jobId, null);
    return () => {
      cancelRetry();
      fetchGenRef.current += 1; // stop the walking chain on unmount
    };
  }, [jobId, statusVersion, fetchPage, cancelRetry]);

  const loadMore = useCallback(async (): Promise<void> => {
    if (!jobId || !cursor || loadingMore) return;
    await fetchPage(jobId, cursor);
  }, [jobId, cursor, loadingMore, fetchPage]);

  const refresh = useCallback(async (): Promise<void> => {
    if (!jobId) return;
    await fetchPage(jobId, null);
  }, [jobId, fetchPage]);

  const addRow = useCallback(
    async (
      itemMpId: number,
      value: string,
      searched?: {
        itemMpText: string;
        matches: SearchedMatch[];
        degraded?: string[];
        variant?: TmVariant;
      },
    ): Promise<void> => {
      if (!jobId) return;
      const row = await mappingApi.addResultRow(jobId, {
        itemMpId,
        value,
        ...(searched
          ? {
              itemMpText: searched.itemMpText,
              matches: searched.matches,
              degraded: searched.degraded,
              variant: searched.variant,
            }
          : {}),
      });
      setRows((prev) => [...prev, row]);
    },
    [jobId],
  );


  const recordEdit = useCallback(
    (rowIndex: number, partial: RowEditInput): number => {
      const seq = ++seqRef.current;
      latestSeqRef.current.set(rowIndex, seq);
      updateEdits((prev) => {
        const next = new Map(prev);
        next.set(rowIndex, { ...partial, editedAt: new Date().toISOString() });
        return next;
      });
      return seq;
    },
    [updateEdits],
  );

  const mergeServerRow = useCallback(
    (rowIndex: number, serverRow: MappingResultRow): void => {
      // A straight replace: the server row is the whole row. It carries no
      // candidates to lose (they live in their own cache), and its `target` is
      // already resolved to whatever pick the edit just stored.
      setRows((prev) =>
        prev.map((row) => (row.rowIndex === rowIndex ? serverRow : row)),
      );
    },
    [],
  );

  /** Merge a single server row into local state (e.g. after a revision
   * restore) — avoids re-fetching the whole result set. */
  const applyServerRow = useCallback(
    (serverRow: MappingResultRow): void => {
      mergeServerRow(serverRow.rowIndex, serverRow);
    },
    [mergeServerRow],
  );

  const dropEdit = useCallback(
    (rowIndex: number): void => {
      updateEdits((prev) => {
        if (!prev.has(rowIndex)) return prev;
        const next = new Map(prev);
        next.delete(rowIndex);
        return next;
      });
    },
    [updateEdits],
  );

  /** Send one edit of a row, after every earlier save of that row settled.
   *  Only the row's newest edit is sent and allowed to touch state: an older
   *  one that finishes late must not drop the newer edit or overwrite its
   *  row (it used to, and a failure of the newer save then had nothing left
   *  to retry). */
  const enqueueSave = useCallback(
    (rowIndex: number, partial: RowEditInput, seq: number): Promise<SaveOutcome> => {
      if (!jobId) return Promise.resolve("failed");
      const jobGen = jobGenRef.current;
      const isCurrent = (): boolean =>
        jobGen === jobGenRef.current &&
        latestSeqRef.current.get(rowIndex) === seq;

      const send = async (): Promise<SaveOutcome> => {
        if (!isCurrent()) return "superseded";
        setSaveState(rowIndex, { state: "saving" });
        try {
          const response = await withSaveRetry(() =>
            mappingApi.patchResult(jobId, rowIndex, toPatchPayload(partial)),
          );
          if (!isCurrent()) return "superseded";
          mergeServerRow(rowIndex, response.row);
          dropEdit(rowIndex);
          setSaveState(rowIndex, { state: "saved" });
          scheduleSavedFade(rowIndex);
          return "saved";
        } catch (err) {
          if (!isCurrent()) return "superseded";
          setSaveState(rowIndex, { state: "failed", error: saveErrorMessage(err) });
          return "failed";
        }
      };

      const queue = rowQueueRef.current;
      const previous = queue.get(rowIndex) ?? Promise.resolve<SaveOutcome>("saved");
      const run = previous.then(send, send);
      queue.set(rowIndex, run);
      void run.finally(() => {
        if (queue.get(rowIndex) === run) queue.delete(rowIndex);
      });
      return run;
    },
    [jobId, mergeServerRow, dropEdit, scheduleSavedFade, setSaveState],
  );

  const applyEdit = useCallback(
    (rowIndex: number, partial: RowEditInput): void => {
      const seq = recordEdit(rowIndex, partial);
      void enqueueSave(rowIndex, partial, seq);
    },
    [recordEdit, enqueueSave],
  );

  const clearEdit = useCallback(
    (rowIndex: number): void => {
      // Anything still queued for the row becomes stale and leaves state alone.
      latestSeqRef.current.set(rowIndex, ++seqRef.current);
      dropEdit(rowIndex);
      setSaveState(rowIndex, null);
    },
    [dropEdit, setSaveState],
  );

  const retrySave = useCallback(
    (rowIndex: number): void => {
      const edit = editsRef.current.get(rowIndex);
      if (!edit) return;
      void enqueueSave(rowIndex, edit, recordEdit(rowIndex, edit));
    },
    [enqueueSave, recordEdit],
  );

  const retryAllFailed = useCallback((): void => {
    for (const rowIndex of [...editsRef.current.keys()]) {
      if (!rowQueueRef.current.has(rowIndex)) retrySave(rowIndex);
    }
  }, [retrySave]);

  const discardUnsaved = useCallback((): void => {
    for (const rowIndex of [...editsRef.current.keys()]) clearEdit(rowIndex);
  }, [clearEdit]);

  const flushSaves = useCallback(
    async (timeoutMs: number): Promise<number[]> => {
      const pending = [...rowQueueRef.current.values()];
      if (pending.length > 0) {
        let timer: number | undefined;
        const timeout = new Promise<void>((resolve) => {
          timer = window.setTimeout(resolve, timeoutMs);
        });
        await Promise.race([Promise.allSettled(pending), timeout]);
        window.clearTimeout(timer);
      }
      return [...editsRef.current.keys()];
    },
    [],
  );

  const bulkApplyEdits = useCallback(
    async (entries: BulkEditEntry[]): Promise<BulkEditResult> => {
      if (entries.length === 0) return { succeeded: 0, failed: 0 };
      if (!jobId) return { succeeded: 0, failed: entries.length };
      let succeeded = 0;
      let failed = 0;
      const queue = entries.map((entry) => ({
        ...entry,
        seq: recordEdit(entry.rowIndex, entry.edit),
      }));

      const worker = async (): Promise<void> => {
        while (queue.length > 0) {
          const entry = queue.shift();
          if (!entry) return;
          const outcome = await enqueueSave(entry.rowIndex, entry.edit, entry.seq);
          if (outcome === "failed") failed += 1;
          else succeeded += 1;
        }
      };

      const workers = Array.from(
        { length: Math.min(BULK_CONCURRENCY, entries.length) },
        () => worker(),
      );
      await Promise.all(workers);
      return { succeeded, failed };
    },
    [jobId, recordEdit, enqueueSave],
  );

  return {
    rows,
    edits,
    saveStates,
    cursor,
    display,
    loading,
    loadingMore,
    error,
    hasMore: cursor !== null,
    notReady,
    applyEdit,
    clearEdit,
    retrySave,
    retryAllFailed,
    discardUnsaved,
    flushSaves,
    bulkApplyEdits,
    loadMore,
    refresh,
    addRow,
    applyServerRow,
  };
};

export const resolveReviewStatus = (
  row: MappingResultRow,
  edit: RowEdit | undefined,
): ReviewStatus => {
  if (edit) return edit.reviewStatus;
  return row.reviewStatus;
};
